import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { useNavigate } from 'react-router-dom';
import back_n from "../Tutorials/Image/back_n.gif";
import back_o from "../Tutorials/Image/back_o.gif";
import back_d from "../Tutorials/Image/back_d.gif";
import principle_n from "../Tutorials/Image/principle_n.gif";
import tutorials_n from "../Tutorials/Image/tutorials_n.gif";
import clue_n from "../Tutorials/Image/clue_n.gif";
import solution_n from "../Tutorials/Image/solution_n.gif";
import solution_o from "../Tutorials/Image/solution_o.gif";
import solution_d from "../Tutorials/Image/solution_d.gif";
import next_n from "../Tutorials/Image/next_n.gif";
import next_o from "../Tutorials/Image/next_o.gif";
import next_d from "../Tutorials/Image/next_d.gif";
import ok_n from "../Tutorials/Image/ok_n.gif";
import ok_o from "../Tutorials/Image/ok_o.gif";
import ok_d from "../Tutorials/Image/ok_d.gif";
import done_o from "../Tutorials/Image/done_o.gif";
import done_n from "../Tutorials/Image/done_n.gif";
import x_close_btn_n from "../Tutorials/Image/x_close_btn_n.png";
import x_close_btn_o from "../Tutorials/Image/x_close_btn_o.png";
import next_step_btn_n from "../Tutorials/Image/next_step_btn_n.png";
import next_step_btn_o from "../Tutorials/Image/next_step_btn_o.png";
import got_it_btn_n from "../Tutorials/Image/got_it_btn_n.png";
import got_it_btn_o from "../Tutorials/Image/got_it_btn_o.png";
import './Style/Question.css';
import Loading from "../../Common/Loading";
import { buildNextQuestionData, buildOptionOrder, buildShuffledQuestionsOrder, saveQuestionPerformance, QuestionPerformancePayload } from '../../services/questionApi';
import ReactDOM from 'react-dom';
import { renderRfcRfic } from './utils/rfcRficUtil';

export interface AnswerRef {
    getAnswer: () => {
        marks: number;
        noOfOptions: number;
        enteredCorrectAnswer: string;
        enteredWrongAnswer: string;
        enteredBlankAnswer: string;
        scoringMethod?: number | null;
        optOrder?: string;
    } | null;
    getSkipMetadata?: () => { noOfOptions: number; optOrder: string } | null;
}

interface ImageQnsProps {
    lessonMeta?: any;
    questionHtml: string;
    currentPage: number;
    totalPages: number;
    goToNextPage?: (e: React.MouseEvent) => void;
    showAlert: (message: string) => void;
    correctAudio: string[];
    incorrectAudio: string[];
    assetsBaseUrl?: string;
    sessionId: number | null;
    isCompleting?: boolean;
    updateSessionPoints?: (pointsEarned: number) => void;
    hwid?: number | null;
    subject?: string;
    // Admin "view lesson" preview support.
    // 'ANSWERS'   -> auto-reveal the correct answer on load (QNS-ANS button)
    // 'SOLUTION'  -> auto-reveal the correct answer AND open the solution (SOLUTION button)
    // 'RFC_RFIC'  -> auto-reveal the correct answer AND show RFC / RFIC reasoning
    // undefined/null -> normal student flow, completely unaffected
    previewMode?: string | null;
    // True whenever this Question page was reached from the sysadmin
    // EL_ViewLesson page. Real students never get this flag.
    isViewLesson?: boolean;
}

const ImageQns: React.ForwardRefRenderFunction<AnswerRef, ImageQnsProps> = (props, ref) => {
    const { lessonMeta, questionHtml, currentPage, totalPages, goToNextPage, showAlert, correctAudio, incorrectAudio, assetsBaseUrl, sessionId, updateSessionPoints, hwid, subject, previewMode, isViewLesson } = props;
    const navigate = useNavigate();
    const iframeRef = useRef<HTMLIFrameElement | null>(null);
    const solnIframeRef = useRef<HTMLIFrameElement>(null);
    const clueIframeRef = useRef<HTMLIFrameElement>(null);
    const prcIframeRef = useRef<HTMLIFrameElement>(null);

    // Is this an admin "view lesson" preview render (as opposed to a real student attempt)?
    const isPreview = previewMode === 'ANSWERS' || previewMode === 'SOLUTION' || previewMode === "RFC_RFIC";
    const viewLesson = !!isViewLesson || new URLSearchParams(window.location.search).get('ViewLesson') === 'Y';

    const [backSrc, setBackSrc] = useState(back_n);
    const [nextSrc, setNextSrc] = useState(next_n);
    const [okSrc, setOkSrc] = useState(ok_n);
    const [doneSrc, setDoneSrc] = useState(done_n);
    const [solutionSrc, setSolutionSrc] = useState(solution_n);
    const [CloseBtnSrc, setCloseBtnSrc] = useState(x_close_btn_n);
    const [NextStepBtnSrc, setNextStepBtnSrc] = useState(next_step_btn_n);
    const [GotItBtnSrc, setGotItBtnSrc] = useState(got_it_btn_n);

    const [okClicked, setOkClicked] = useState(false);
    // Students lose Back / Clue / Principle / Tutorial after answering.
    // Admin ViewLesson preview keeps them visible.
    const lockAfterAnswer = okClicked && !(viewLesson && isPreview);

    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [currentQnMeta, setCurrentQnMeta] = useState<any | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [startTime, setStartTime] = useState<Date | null>(null);

    const [showClueModal, setShowClueModal] = useState(false);
    const [clueContent, setClueContent] = useState('');
    const [isClueLoading, setIsClueLoading] = useState(false);
    const [noOfClues, setNoOfClues] = useState(0);
    const [currentClueNo, setCurrentClueNo] = useState(0);
    const [clueSeen, setClueSeen] = useState(false);

    const [showPrcModal, setShowPrcModal] = useState(false);
    const [prcContent, setPrcContent] = useState("");
    const [isPrcLoading, setIsPrcLoading] = useState(false);
    const [prcSeen, setPrcSeen] = useState(false);
    const [principleList, setPrincipleList] = useState<string[]>([]);
    const [currentPrcIndex, setCurrentPrcIndex] = useState(0);

    const [showSolnModal, setShowSolnModal] = useState(false);
    const [isSolnLoading, setIsSolnLoading] = useState(false);
    const [solnContent, setSolnContent] = useState("");
    const [currentSolnNo, setCurrentSolnNo] = useState(0);
    const [revealedSolnSteps, setRevealedSolnSteps] = useState<number[]>([]);
    useEffect(() => {
        setCurrentSolnNo(0);
        setRevealedSolnSteps([]);
    }, [currentQnMeta?.QnNo]);
    const [noOfSteps, setNoOfSteps] = useState(0);
    const [solutionSeen, setSolutionSeen] = useState<string | number>('N');

    // Guards against re-running the auto-reveal more than once per question in preview mode
    const revealedForPageRef = useRef<number | null>(null);

    const search = window.location.search;
    const params = new URLSearchParams(search);
    const folderParam = params.get('folder');
    const lessonName = params.get('name');
    const diccode = params.get('diccode');
    const calledFrom = params.get('calledFrom') || params.get('CalledFrom');
    const isInAssessment = window.location.href.includes('Assessment');

    const handleBackMouseOver = () => setBackSrc(back_o);
    const handleBackMouseOut = () => setBackSrc(back_n);
    const handleBackMouseDown = () => setBackSrc(back_d);
    const handleBackMouseUp = () => setBackSrc(back_o);
    const handleNextMouseOver = () => setNextSrc(next_o);
    const handleNextMouseOut = () => setNextSrc(next_n);
    const handleNextMouseDown = () => setNextSrc(next_d);
    const handleNextMouseUp = () => setNextSrc(next_o);
    const handleOkMouseOver = () => setOkSrc(ok_o);
    const handleOkMouseOut = () => setOkSrc(ok_n);
    const handleOkMouseDown = () => setOkSrc(ok_d);
    const handleOkMouseUp = () => setOkSrc(ok_o);
    const handleSolutionMouseOver = () => setSolutionSrc(solution_o);
    const handleSolutionMouseOut = () => setSolutionSrc(solution_n);
    const handleSolutionMouseDown = () => setSolutionSrc(solution_d);
    const handleCloseBtnMouseOver = () => setCloseBtnSrc(x_close_btn_o);
    const handleCloseBtnMouseOut = () => setCloseBtnSrc(x_close_btn_n);
    const handleNextStepBtnMouseOver = () => setNextStepBtnSrc(next_step_btn_o);
    const handleNextStepBtnMouseOut = () => setNextStepBtnSrc(next_step_btn_n);
    const handleGotItBtnMouseOver = () => setGotItBtnSrc(got_it_btn_o);
    const handleGotItBtnMouseOut = () => setGotItBtnSrc(got_it_btn_n);


    const playRandomSound = useCallback(async (soundFiles: string[], folder: 'Correct' | 'Incorrect') => {
        const baseUrl = assetsBaseUrl || "https://www.elearnoncloud.com/Storage/assets/";
        if (!soundFiles || soundFiles.length === 0) return;

        const fileName = soundFiles[Math.floor(Math.random() * soundFiles.length)];
        const audioUrl = `${baseUrl}Audio/Question/${folder}/${fileName}`;

        const audio = new Audio(audioUrl);
        try {
            await audio.play();
        } catch (error) {
            console.error(`Error playing audio from ${audioUrl}:`, error);
        }
    }, [assetsBaseUrl]);


    const handleClueClick = useCallback(async () => {
        if (!currentQnMeta || !lessonMeta) return;

        setShowClueModal(true);
        setIsClueLoading(true);
        setClueContent("");
        setCurrentClueNo(0);
        setNoOfClues(0);

        const searchParams = new URLSearchParams(window.location.search);
        const folderParam = searchParams.get('folder');
        const countryParam = params.get('country') || 'Australia';
        const varValuesParam = currentQnMeta?.VarValues || "";

        if (!folderParam) {
            showAlert("Could not determine the lesson folder.");
            setIsClueLoading(false);
            setShowClueModal(false);
            return;
        }

        const accessToken = localStorage.getItem('access_token');
        const headers: HeadersInit = { 'Content-Type': 'application/json' };
        if (accessToken) {
            headers['Authorization'] = `Bearer ${accessToken}`;
        }

        const apiUrl = process.env.REACT_APP_DOCUMENT_LINK;

        try {
            const response = await fetch(`${apiUrl}clue/`, {
                method: 'POST',
                headers: headers,
                body: JSON.stringify({
                    folder: folderParam,
                    questionNo: currentQnMeta.QnNo,
                    country: countryParam,
                    varValues: varValuesParam,
                }),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            } else {
                const data = await response.json();
                setClueContent(data.clueHtml || "");
                setClueSeen(true);
            }

        } catch (error) {
            console.error("Error fetching clue:", error);
            setClueContent("");
        } finally {
            setIsClueLoading(false);
        }
    }, [currentQnMeta, lessonMeta, showAlert, params]);

    const fetchPrincipleContent = async (principleId: string, folderParam: string, countryParam: string) => {
        const accessToken = localStorage.getItem('access_token');
        const headers: HeadersInit = { 'Content-Type': 'application/json' };
        if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;

        const apiUrl = process.env.REACT_APP_DOCUMENT_LINK;

        const response = await fetch(`${apiUrl}principle/`, {
            method: 'POST',
            headers: headers,
            body: JSON.stringify({
                folder: folderParam,
                questionNo: principleId,
                country: countryParam,
            }),
        });

        if (!response.ok) throw new Error("Failed");
        return await response.json();
    };

    const handlePrcClick = useCallback(async () => {
        if (!currentQnMeta || !lessonMeta) return;

        const prcString = currentQnMeta.PrincipleNos || "";
        const prcIds = prcString.replace(/"/g, "").split(',').filter((s: string) => s.trim() !== "");

        // 2. Fallback Logic
        if (prcIds.length === 0) {
            if ((currentQnMeta.NoOfPrc || 0) > 0) {
                prcIds.push("1");
            } else {
                showAlert("No principles found for this question.");
                return;
            }
        }

        setShowPrcModal(true);
        setIsPrcLoading(true);
        setPrcContent("");
        setPrincipleList(prcIds);
        setCurrentPrcIndex(0);

        const searchParams = new URLSearchParams(window.location.search);
        const folderParam = searchParams.get('folder');
        const countryParam = params.get('country') || 'Australia';

        if (!folderParam) {
            showAlert("Could not determine the lesson folder.");
            setIsPrcLoading(false);
            setShowPrcModal(false);
            return;
        }

        try {
            const data = await fetchPrincipleContent(prcIds[0], folderParam, countryParam);
            setPrcContent(data.principleHtml || "");
            setPrcSeen(true);
        } catch (error) {
            console.error("Error fetching principle:", error);
            setPrcContent("");
        } finally {
            setIsPrcLoading(false);
        }
    }, [currentQnMeta, lessonMeta, showAlert, params]);

    const handleSolnClick = useCallback(async (autoExpand = false) => {
        if (!currentQnMeta || !lessonMeta) return;

        setShowSolnModal(true);
        setIsSolnLoading(true);
        setSolnContent("");
        setNoOfSteps(0);

        const searchParams = new URLSearchParams(window.location.search);
        const folderParam = searchParams.get('folder');
        const countryParam = params.get('country') || 'Australia';

        if (!folderParam) {
            showAlert("Could not determine the lesson folder.");
            setIsSolnLoading(false);
            setShowSolnModal(false);
            return;
        }

        const accessToken = localStorage.getItem('access_token');
        const headers: HeadersInit = { 'Content-Type': 'application/json' };
        if (accessToken) {
            headers['Authorization'] = `Bearer ${accessToken}`;
        }

        const apiUrl = process.env.REACT_APP_DOCUMENT_LINK;

        try {
            const response = await fetch(`${apiUrl}solution/`, {
                method: 'POST',
                headers: headers,
                body: JSON.stringify({
                    folder: folderParam,
                    questionNo: currentQnMeta.QnNo,
                    country: countryParam,
                    autoExpand: autoExpand,
                }),
            });

            // No solution file for this question.
            if (response.status === 404) {
                setShowSolnModal(false);
                showAlert("No solution found for this question.");
                return;
            }

            if (!response.ok) {
                if (response.status === 401 || response.status === 403) {
                    showAlert("Authentication failed. Please log in again.");
                }
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            // 200 with an error body or empty solution.
            if (data?.error || !data?.solutionHtml) {
                setShowSolnModal(false);
                showAlert("No solution found for this question.");
                return;
            }

            setSolnContent(data.solutionHtml);
            if (solutionSeen === 'N') {
                setSolutionSeen(1);
            }

        } catch (error) {
            console.error("Error fetching solution:", error);
            setShowSolnModal(false);
        } finally {
            setIsSolnLoading(false);
        }
    }, [currentQnMeta, lessonMeta, showAlert, solutionSeen]);

    // Keeps the latest handleSolnClick available to the preview reveal.
    const handleSolnClickRef = useRef(handleSolnClick);
    useEffect(() => {
        handleSolnClickRef.current = handleSolnClick;
    }, [handleSolnClick]);

    const injectImageQnBridge = (html: string): string => {
        let cleaned = html
            .replace(/onmousemove="[^"]*"/gi, "")
            .replace(/<script[\s\S]*?<\/script>/gi, "");

        const bridge = `
        <style>
            body { font-family: Arial, sans-serif; margin: 0; padding: 0; }
            
            /* Fix for extra margin-top on question text */
            p { margin: 0; padding: 0; }
            table { border-collapse: collapse; border-spacing: 0; }

            td[id="KSD_TD2"] table td {
                height: 0px !important;
            }

            td[id^="KSD_TD2"] table table {
                width: auto !important;
                display: inline-table !important;
            }

            #el-options-container table, table[id^='KSD_TABLE'] { 
                border-collapse: separate !important; 
                border-spacing: 0 !important; 
                width: 100%;
            }

            #el-options-container.image-options-container {
                padding: 0 10px;
            }
    
            .option-row {
                cursor: pointer;
                border-radius: 12px;
                overflow: hidden;
                transition: background-color 0.2s ease-in-out;
            }
            .option-row:nth-child(odd) { background-color: #F8F8F8; }
            .option-row:nth-child(even) { background-color: #fdf5ec; }
            .option-selected { background-color: #7A96DF !important; }

            table[id^="KSD_TABLE"].ksd-small-objective-oimage tr[id^="KSD_TR"] {
                min-height: 50px !important;
            }
            table[id^="KSD_TABLE"].ksd-small-objective-oimage tr[id^="KSD_TR"] > td {
                height: 50px !important;
                vertical-align: middle !important;
            }
            
            td[id^="KSD_TD"] { 
                cursor: pointer; 
                padding-top: 5px !important; 
                padding-bottom: 5px !important; 
                padding-left: 5px !important;
                height: 0px !important; 
            }

            tr[id^="KSD_TR"] { cursor: pointer; }
            td[id^="KSD_TD1"] { display: none; }
        </style>
        <script>
            (function() {
                function bridgeLogic() {
                  try {
                    const optionsTable = document.querySelector("table[id^='KSD_TABLE']");
                    if (!optionsTable) { 
                      setTimeout(bridgeLogic, 50);
                      return;
                    }

                    const questionInfo = document.querySelector('#KSD_MW_Info');
                    const optionImageType = parseInt(questionInfo?.value?.split('|')[8] || '', 10);
                    const detectedQuestionType = document.querySelector('#KSD_DETECTED_QN_TYPE')?.value;
                    if (detectedQuestionType?.toUpperCase() === 'O_IMAGE' && (optionImageType === 4 || optionImageType === 7)) {
                        optionsTable.classList.add('ksd-small-objective-oimage');
                    }

                    const hasDividerBar = document.querySelector("[id*='KSD_DIVIDERBAR']");
                    if (hasDividerBar) {
                        const style = document.createElement("style");
                        style.innerHTML = \`
                            table[id^="KSD_TABLE"] tbody {
                                display: grid !important;
                                grid-auto-rows: auto;
                                row-gap: 0px !important;
                            }
                            tr[id^="KSD_TR"] {
                                display: block !important;
                                margin-bottom: 10px !important;
                            }
                        \`;
                        document.head.appendChild(style);
                    }

                    const container = optionsTable.parentElement;
                    if (container) {
                        container.id = 'el-options-container';
                        if (detectedQuestionType?.toUpperCase() === 'O_IMAGE') {
                            container.classList.add('image-options-container');
                        }
                    }
    
                    let normalBgUrl = optionsTable.getAttribute('data-normal-bg');
                    let selectedBgUrl = optionsTable.getAttribute('data-selected-bg');

                    // O_IMAGE questions identify their option artwork in the ninth
                    // KSD_MW_Info field (index 8), even when the API HTML does not
                    // provide data-normal-bg/data-selected-bg attributes.
                    if (!normalBgUrl) {
                        const optionImages = {
                            2: ['large_objective_n.png', 'large_objective_d.png'],
                            3: ['medium_objective_n.png', 'medium_objective_d.png'],
                            4: ['small_objective_n.png', 'small_objective_d.png'],
                            5: ['large_objective_shortened_n.png', 'large_objective_shortened_d.png'],
                            6: ['medium_objective_shortened_n.png', 'medium_objective_shortened_d.png'],
                            7: ['small_objective_shortened_n.png', 'small_objective_shortened_d.png']
                        };
                        const imagePair = optionImages[optionImageType];
                        const divider = document.querySelector('#KSD_DIVIDERBAR');
                        const dividerImage = divider && getComputedStyle(divider).backgroundImage;
                        const dividerUrl = dividerImage && dividerImage.match(/^url\\(["']?(.*?)["']?\\)$/);
                        if (imagePair && dividerUrl) {
                            const imageDirectory = dividerUrl[1].replace(/divider_bar\\.png(?:[?#].*)?$/, '');
                            normalBgUrl = imageDirectory + imagePair[0];
                            selectedBgUrl = imageDirectory + imagePair[1];
                        }
                    }
                    const hasCustomImages = !!normalBgUrl; 

                    let selectedOptionElement = null;

                    const allRows = document.querySelectorAll('tr[id^="KSD_TR"]');
        
                    allRows.forEach((row, index) => {
                       const labelCell = row.querySelector('td[id="KSD_TD1"]');
                       if(labelCell) labelCell.style.color = 'black';

                       if (!hasCustomImages) {
                           row.classList.add('option-row'); 
                           row.removeAttribute('style'); 
                       } else {
                           const optionCell = row.querySelector('td[id^="KSD_TD"]');
                           if (optionCell) {
                               optionCell.style.backgroundImage = 'url("' + normalBgUrl + '")';
                               optionCell.style.backgroundSize = '100% 100%';
                               optionCell.style.backgroundRepeat = 'no-repeat';
                           }
                           const contentCell = row.querySelector('td[id="KSD_TD2"]');
                           if (contentCell) {
                               contentCell.style.textAlign = 'center';
                               contentCell.style.verticalAlign = 'middle';
                           }
                           row.style.cursor = 'pointer';
                       }
                    });
    
                    allRows.forEach((row, index) => {
                      const optionCell = row.querySelector('td[id^="KSD_TD"]');
                      const radio = row.querySelector('input[id^="KSD_Rad_"]');
                      
                      let postableValue = null;
                      if (radio) {
                          postableValue = radio.getAttribute('name');
                      }
                      if (postableValue === null || postableValue === "") {
                        postableValue = String(index);
                      }

                      const clickHandler = (e) => {
                        e.stopPropagation();
                        e.preventDefault();

                        if (!hasCustomImages) {
                            allRows.forEach(r => r.classList.remove('option-selected'));
                            row.classList.add('option-selected');
                        } else {
                            if (selectedOptionElement && selectedOptionElement !== optionCell) {
                                if (normalBgUrl) {
                                    selectedOptionElement.style.backgroundImage = 'url("' + normalBgUrl + '")';
                                }
                            }
                            if (optionCell && selectedBgUrl) {
                                optionCell.style.backgroundImage = 'url("' + selectedBgUrl + '")';
                                optionCell.style.backgroundSize = '100% 100%';
                                optionCell.style.backgroundRepeat = 'no-repeat';
                            }
                            selectedOptionElement = optionCell;
                        }

                        if (radio) radio.checked = true;

                        parent.postMessage({ type: 'el-option-selected', value: postableValue }, '*');
                      };

                      row.addEventListener('click', clickHandler);
                    });
                  } catch(e) { console.error("ImageQns Bridge Error:", e); }
                }
                bridgeLogic();
            })();
        </script>
    `;
        return cleaned.includes("</body>") ? cleaned.replace("</body>", bridge + "</body>") : cleaned + bridge;
    };

    useEffect(() => {
        setSelectedOption(null);
        setOkClicked(false);
        setStartTime(new Date());
        setSolutionSeen('N');
        setClueSeen(false);
        setPrcSeen(false);
        revealedForPageRef.current = null;
        if (lessonMeta?.qns) {
            setCurrentQnMeta(lessonMeta.qns[currentPage - 1]);
        }
    }, [currentPage, lessonMeta]);

    useEffect(() => {
        const iframe = iframeRef.current;
        if (iframe && questionHtml) {
            iframe.srcdoc = injectImageQnBridge(questionHtml);
        }
    }, [questionHtml]);

    useEffect(() => {
        const onMessage = (e: MessageEvent) => {
            if (e.data?.type === 'el-option-selected') {
                setSelectedOption(String(e.data.value));
            }
        };
        window.addEventListener('message', onMessage);
        return () => window.removeEventListener('message', onMessage);
    }, []);

    // ── ADMIN PREVIEW ONLY ────────────────────────────────────────────────
    // Marks the correct option (green check) and shows the explanation,
    // like a real correct OK click, but WITHOUT saving anything.
    const autoRevealCorrectAnswer = useCallback(() => {
        const iframeDoc = iframeRef.current?.contentDocument;
        if (!iframeDoc) return;

        let correctAnswerIndex = iframeDoc.getElementById('KSD_Ans')?.getAttribute('value');
        if (!correctAnswerIndex) {
            const info = (iframeDoc.getElementById('KSD_MW_Info') as HTMLInputElement)?.value?.split('|');
            if (info && info.length > 3) correctAnswerIndex = info[3];
        }
        if (!correctAnswerIndex) {
            // Still let the admin move on.
            setOkClicked(true);
            return;
        }

        setSelectedOption(correctAnswerIndex);

        const CORRECT_URL = "https://www.elearnoncloud.com/Storage/assets/Image/Questions/EL_O/AnsCorrect.GIF";

        const allRows = iframeDoc.querySelectorAll('tr[id^="KSD_TR_"]');
        allRows.forEach(row => {
            (row as HTMLElement).style.pointerEvents = 'none';

            const radio = row.querySelector('input[id^="KSD_Rad_"]');
            const optionIndex = radio ? radio.getAttribute('name') : null;
            if (optionIndex === null) return;

            const ansCell = row.querySelector(`td[id^="KSD_TDANS_${optionIndex}"]`);
            if (ansCell) {
                (ansCell as HTMLElement).style.display = '';
                (ansCell as HTMLElement).style.textAlign = 'center';
                (ansCell as HTMLElement).style.verticalAlign = 'middle';

                if (optionIndex === correctAnswerIndex) {
                    const img = iframeDoc.createElement("img");
                    img.src = CORRECT_URL;
                    img.style.verticalAlign = "middle";
                    ansCell.innerHTML = "";
                    ansCell.appendChild(img);
                }
            }
        });

        const explanationElement = iframeDoc.getElementById(`TD_Rsn_${correctAnswerIndex}`);
        if (explanationElement) {
            const explanationHtml = explanationElement.innerHTML;

            if (explanationHtml && !iframeDoc.getElementById("el-explanation-container")) {
                const container = iframeDoc.createElement("div");
                container.id = "el-explanation-container";

                container.innerHTML = `
                    <hr style='height:1px; background-color:#cfcfcf; border:none; margin-top:50px; margin-bottom:30px; margin-left:30px; margin-right:30px;'>
                    <p style='font-size:24px; color:#797979; font-family:Arial,Helvetica,sans-serif; line-height:30px; padding-left:30px; padding-right:30px; margin-top:0px; margin-bottom:20px'>Explanation</p>
                    <p style='font-size:24px; color:#000000; font-family:Arial,Helvetica,sans-serif; line-height:30px; padding-left:30px; padding-right:30px; margin-top:0px; margin-bottom:50px'>
                        ${explanationHtml}
                    </p>
                `;
                iframeDoc.body.appendChild(container);

                setTimeout(() => {
                    iframeDoc.documentElement.scrollTop = iframeDoc.documentElement.scrollHeight;
                }, 100);
            }
        }

        setOkClicked(true);

        if (previewMode === 'SOLUTION' && currentQnMeta?.NoOfSol && currentQnMeta.NoOfSol > 0) {
            handleSolnClickRef.current(true);
        }
    }, [previewMode, currentQnMeta]);
    // ── END ADMIN PREVIEW ONLY ───────────────────────────────────────────

    const validateLocalAnswer = () => {
        if (selectedOption == null) {
            showAlert("Please answer the question.");
            return null;
        }

        const iframeDoc = iframeRef.current?.contentDocument;
        if (!iframeDoc) return null;

        let correctAnswerIndex = iframeDoc.getElementById('KSD_Ans')?.getAttribute('value');
        if (!correctAnswerIndex) {
            const info = (iframeDoc.getElementById('KSD_MW_Info') as HTMLInputElement)?.value.split('|');
            if (info && info.length > 3) correctAnswerIndex = info[3];
        }

        if (!correctAnswerIndex) {
            showAlert("Error: Could not determine correct answer.");
            return null;
        }

        const isCorrect = String(selectedOption) === String(correctAnswerIndex);
        const marks = isCorrect ? 1 : 0;
        const allRows = iframeDoc.querySelectorAll('tr[id^="KSD_TR_"]');

        return {
            marks,
            noOfOptions: allRows.length,
            enteredCorrectAnswer: isCorrect ? selectedOption : "",
            enteredWrongAnswer: !isCorrect ? selectedOption : "",
            enteredBlankAnswer: "",
            optOrder: buildOptionOrder(iframeRef.current, 'O'),
            scoringMethod: null,
            correctAnswerIndex
        };
    };

    const getSkipMetadata = useCallback(() => {
        const iframeDoc = iframeRef.current?.contentDocument;
        if (!iframeDoc) return null;

        const noOfOptions = iframeDoc.querySelectorAll('tr[id^="KSD_TR_"]').length;
        if (noOfOptions === 0) return null;

        return {
            noOfOptions,
            optOrder: buildOptionOrder(iframeRef.current, 'O'),
        };
    }, []);

    useImperativeHandle(ref, () => ({
        getAnswer: validateLocalAnswer,
        getSkipMetadata,
    }), [validateLocalAnswer, getSkipMetadata]);

    const handleDbUpdate = useCallback((payload: QuestionPerformancePayload) => {
        // Never persist performance/points data for admin preview renders.
        if (isPreview) {
            return Promise.resolve(null as any);
        }
        return saveQuestionPerformance(payload)
            .then((data) => {
                if (currentPage === totalPages && updateSessionPoints) {
                    updateSessionPoints(data.pointsEarned || 0);
                }
                return data;
            })
            .catch((error) => {
                console.error("Save Performance Error:", error);
                throw error;
            });
    }, [currentPage, totalPages, updateSessionPoints, isPreview]);


    // ── ADMIN PREVIEW ONLY (RFC/RFIC) ───────────────────────────────────────
    // Shows every option's reasoning at once: correct option first (RFC),
    // the rest labelled RFIC. If the question has no TD_Rsn_ elements, falls
    // back to the shared renderRfcRfic helper (KSD_MW_Info parts). The answer
    // is always revealed and the page always flips to answered, so the
    // buttons keep working.
    const autoRevealRfcRfic = useCallback(() => {
        const iframeDoc = iframeRef.current?.contentDocument;
        if (!iframeDoc) return;

        let correctAnswerIndex = iframeDoc.getElementById('KSD_Ans')?.getAttribute('value') || '';
        if (!correctAnswerIndex) {
            const info = (iframeDoc.getElementById('KSD_MW_Info') as HTMLInputElement)?.value?.split('|');
            if (info && info.length > 3) correctAnswerIndex = info[3];
        }
        if (!correctAnswerIndex) {
            setOkClicked(true);
            return;
        }

        setSelectedOption(correctAnswerIndex);

        const CORRECT_URL = "https://www.elearnoncloud.com/Storage/assets/Image/Questions/EL_O/AnsCorrect.GIF";

        const allRows = iframeDoc.querySelectorAll('tr[id^="KSD_TR_"]');
        allRows.forEach(row => {
            (row as HTMLElement).style.pointerEvents = 'none';

            const radio = row.querySelector('input[id^="KSD_Rad_"]');
            const optionIndex = radio ? radio.getAttribute('name') : null;
            if (optionIndex === null) return;

            const ansCell = row.querySelector(`td[id^="KSD_TDANS_${optionIndex}"]`);
            if (ansCell) {
                (ansCell as HTMLElement).style.display = '';
                (ansCell as HTMLElement).style.textAlign = 'center';
                (ansCell as HTMLElement).style.verticalAlign = 'middle';

                if (optionIndex === correctAnswerIndex) {
                    const img = iframeDoc.createElement("img");
                    img.src = CORRECT_URL;
                    img.style.verticalAlign = "middle";
                    ansCell.innerHTML = "";
                    ansCell.appendChild(img);
                }
            }
        });

        const rsnElements = Array.from(iframeDoc.querySelectorAll('[id^="TD_Rsn_"]')) as HTMLElement[];

        if (rsnElements.length === 0) {
            // No per-option reasoning: try question-level explanations.
            renderRfcRfic(iframeDoc);
        } else if (!iframeDoc.getElementById("el-explanation-container")) {
            const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

            const items = rsnElements
                .map((el, index) => {
                    const idSuffix = el.id.replace("TD_Rsn_", "");
                    const html = el.innerHTML.trim();

                    return html
                        ? {
                            id: idSuffix,
                            option: letters[index],   // visual order
                            content: html,
                        }
                        : null;
                })
                .filter(
                    (x): x is { id: string; option: string; content: string } =>
                        x !== null
                );

            if (items.length > 0) {
                const correctItem = items.find(i => i.id === correctAnswerIndex);
                const remainingItems = items.filter(i => i.id !== correctAnswerIndex);
                const orderedItems = correctItem
                    ? [correctItem, ...remainingItems]
                    : remainingItems;

                const firstHtml = `
<i>Option ${orderedItems[0].option}</i><br>
${orderedItems[0].content}
`;
                const remainingHtml = orderedItems
                    .slice(1)
                    .map(item => `
        <i>Option ${item.option}</i><br>
        ${item.content}<br><br>
    `)
                    .join('');

                const container = iframeDoc.createElement("div");
                container.id = "el-explanation-container";
                container.innerHTML = `
            <hr style='height:1px; background-color:#cfcfcf; border:none; margin-top:50px; margin-bottom:30px; margin-left:30px; margin-right:30px;'>
            <p style='font-size:24px; color:#797979; font-family:Arial,Helvetica,sans-serif; line-height:30px; padding-left:30px; padding-right:30px; margin-top:0px; margin-bottom:20px'>Explanation</p>
            <p style='font-size:24px; color:#000000; font-family:Arial,Helvetica,sans-serif; line-height:30px; padding-left:30px; padding-right:30px; margin-top:0px; margin-bottom:50px'>
                <span style="font-weight:bold"> RFC</span><br>${firstHtml}<br><span style="font-weight:bold">${remainingHtml ? 'RFIC' : ''}</span><br>${remainingHtml}
            </p>
        `;
                iframeDoc.body.appendChild(container);
                setTimeout(() => {
                    iframeDoc.documentElement.scrollTop = iframeDoc.documentElement.scrollHeight;
                }, 100);
            }
        }

        setOkClicked(true);
    }, []);

    const handleQuestionIframeLoad = useCallback(() => {
        if (!isPreview) return;
        if (revealedForPageRef.current === currentPage) return; // already revealed for this question
        revealedForPageRef.current = currentPage;

        if (previewMode === 'RFC_RFIC') {
            autoRevealRfcRfic();
        } else {
            // 'ANSWERS' and 'SOLUTION'
            autoRevealCorrectAnswer();
        }
    }, [isPreview, previewMode, currentPage, autoRevealCorrectAnswer, autoRevealRfcRfic]);

    const handleOkClick = async (e: React.MouseEvent<any>) => {
        e.preventDefault();
        if (okClicked || isSubmitting) return;

        const answerData = validateLocalAnswer();
        if (!answerData) return;

        const { marks, noOfOptions, enteredCorrectAnswer, enteredWrongAnswer, correctAnswerIndex } = answerData;
        const isCorrect = marks === 1;

        const questionAnswerTime = startTime ? Math.floor((new Date().getTime() - startTime.getTime()) / 1000) : 0;

        const payload: QuestionPerformancePayload = {
            schoolCode: sessionStorage.getItem('SchoolCode') || '',
            familyCode: sessionStorage.getItem('FamilyCode') || '',
            login: sessionStorage.getItem('Login') || '',
            dicCode: sessionStorage.getItem('SelectedDICFile') || '',
            lsnTestCode: sessionStorage.getItem('LsnTestCode') || '',
            sessionId: sessionId || 1,
            qnNo: currentQnMeta?.QnNo,
            subNo: 0,
            qnType: currentQnMeta?.QnType,
            marks: marks,
            noOfOptions: noOfOptions,
            enteredCorrectAnswer: enteredCorrectAnswer,
            enteredWrongAnswer: enteredWrongAnswer,
            enteredBlankAnswer: "",
            solutionSeen: (typeof solutionSeen === 'number' && solutionSeen > 0) || solutionSeen === 'Y' ? 'Y' : 'N',
            clueSeen: clueSeen,
            principleSeen: prcSeen,
            questionLoadTime: 0,
            questionAnswerTime: questionAnswerTime,
            lastQnInd: currentQnMeta?.LastQnInd || currentPage,
            nextQnData: buildNextQuestionData(lessonMeta?.qns, currentPage),
            dbgShuffledQnsOrder: buildShuffledQuestionsOrder(lessonMeta?.qns),
            allQuestionsCompleted: currentPage === totalPages ? 'True' : 'False',
            chapterCode: sessionStorage.getItem('ChapterCode') || '',
            unitCode: sessionStorage.getItem('UnitCode') || '',
            optOrder: buildOptionOrder(iframeRef.current, 'O'),
            scoringMethod: null,
            hwid: hwid,
            subject: subject
        };

        const isLastQuestion = currentPage === totalPages;

        if (isLastQuestion) {
            // BLOCKING: Show Loading, Wait for DB, then proceed
            setIsSubmitting(true);
            try {
                await handleDbUpdate(payload);
            } catch (error) {
                console.error("Final submit failed", error);
                showAlert("Error saving progress. Please try again.");
                setIsSubmitting(false);
                return;
            }
            setIsSubmitting(false);
        } else {
            handleDbUpdate(payload).catch(e => console.error("Background save failed", e));
        }

        const iframeDoc = iframeRef.current?.contentDocument;
        if (iframeDoc) {
            const CORRECT_URL = "https://www.elearnoncloud.com/Storage/assets/Image/Questions/EL_O/AnsCorrect.GIF";
            const WRONG_URL = "https://www.elearnoncloud.com/Storage/assets/Image/Questions/EL_O/AnsWrong.GIF";
            const SHOULDBE_URL = "https://www.elearnoncloud.com/Storage/assets/Image/Questions/EL_O/AnsShouldbe.GIF";

            const allRows = iframeDoc.querySelectorAll('tr[id^="KSD_TR_"]');
            allRows.forEach(row => {
                (row as HTMLElement).style.pointerEvents = 'none';

                const radio = row.querySelector('input[id^="KSD_Rad_"]');
                const optionIndex = radio ? radio.getAttribute('name') : null;
                if (optionIndex === null) return;

                const ansCell = row.querySelector(`td[id^="KSD_TDANS_${optionIndex}"]`);

                if (ansCell) {
                    (ansCell as HTMLElement).style.display = '';
                    (ansCell as HTMLElement).style.textAlign = 'center';
                    (ansCell as HTMLElement).style.verticalAlign = 'middle';

                    const isThisRowCorrectAnswer = (optionIndex === correctAnswerIndex);
                    const isThisRowSelected = (optionIndex === selectedOption);

                    let imgUrl = "";

                    if (isCorrect) {
                        if (isThisRowCorrectAnswer) imgUrl = CORRECT_URL;
                    } else {
                        if (isThisRowSelected) imgUrl = WRONG_URL;
                        if (isThisRowCorrectAnswer) imgUrl = SHOULDBE_URL;
                    }

                    if (imgUrl) {
                        const img = iframeDoc.createElement("img");
                        img.src = imgUrl;
                        img.style.verticalAlign = "middle";
                        ansCell.innerHTML = "";
                        ansCell.appendChild(img);
                    }
                }
            });

            const explanationElement = iframeDoc.getElementById(`TD_Rsn_${selectedOption}`);

            if (explanationElement) {
                const explanationHtml = explanationElement.innerHTML;

                if (explanationHtml && !iframeDoc.getElementById("el-explanation-container")) {
                    const container = iframeDoc.createElement("div");
                    container.id = "el-explanation-container";

                    container.innerHTML = `
                    <hr style='height:1px; background-color:#cfcfcf; border:none; margin-top:50px; margin-bottom:30px; margin-left:30px; margin-right:30px;'>
                    <p style='font-size:24px; color:#797979; font-family:Arial,Helvetica,sans-serif; line-height:30px; padding-left:30px; padding-right:30px; margin-top:0px; margin-bottom:20px'>Explanation</p>
                    <p style='font-size:24px; color:#000000; font-family:Arial,Helvetica,sans-serif; line-height:30px; padding-left:30px; padding-right:30px; margin-top:0px; margin-bottom:50px'>
                        ${explanationHtml}
                    </p>
                `;
                    iframeDoc.body.appendChild(container);

                    setTimeout(() => {
                        iframeDoc.documentElement.scrollTop = iframeDoc.documentElement.scrollHeight;
                    }, 100);
                }
            }
        }

        if (marks === 1) {
            playRandomSound(correctAudio, 'Correct');
        } else {
            playRandomSound(incorrectAudio, 'Incorrect');
            if (currentQnMeta?.NoOfSol && currentQnMeta.NoOfSol > 0) {
                handleSolnClick(true);
            }
        }

        setOkClicked(true);
    };

    const handleBack = () => {
        const cf = params.get("calledFrom") || params.get("CalledFrom");
        if (cf) {
            window.location.href = decodeURIComponent(cf);
        } else if (window.history.length > 1) {
            window.history.back();
        }
    };

    // ── ADMIN PREVIEW ONLY ────────────────────────────────────────────────
    // When opened from sysadmin's EL_ViewLesson, "Done" goes straight back
    // to that page. Real students keep the normal Result flow.
    const handleDoneClick = () => {
        if (viewLesson) {
            const cf = params.get('CalledFrom') || params.get('calledFrom') || '';
            if (cf) {
                window.location.href = decodeURIComponent(cf);
            } else if (window.history.length > 1) {
                window.history.back();
            }
            return;
        }

        const dicCode = sessionStorage.getItem('SelectedDICFile') || '';
        const lsnTestCode = sessionStorage.getItem('LsnTestCode') || '';
        navigate(
            `/STUDENT/Tutorials/Result?DicCode=${dicCode}&LsnTestCode=${lsnTestCode}&SessionID=${sessionId}&source=lesson&complete=1` +
            (calledFrom ? `&calledFrom=${encodeURIComponent(calledFrom)}` : "")
        );
    };
    // ── END ADMIN PREVIEW ONLY ───────────────────────────────────────────

    // Tutorial link: ViewLesson goes to TutorialsCopy and keeps its params,
    // students keep the original link.
    const tutorialHref = viewLesson
        ? `/STUDENT/TutorialsCopy/Tutorial?LsnTestCode=${encodeURIComponent(folderParam ?? '')}&folder=${encodeURIComponent(folderParam ?? '')}&name=${encodeURIComponent(lessonName ?? '')}&diccode=${encodeURIComponent(diccode ?? '')}&CalledFrom=${encodeURIComponent(params.get('CalledFrom') || params.get('calledFrom') || '')}&ViewLesson=Y${previewMode ? `&PreviewMode=${encodeURIComponent(previewMode)}` : ''}${params.get('QnNo') ? `&QnNo=${encodeURIComponent(params.get('QnNo') as string)}` : ''}`
        : `/STUDENT/Tutorials/Tutorial?id=${folderParam}&name=${encodeURIComponent(lessonName ?? '')}&folder=${encodeURIComponent(diccode ?? '')}&calledFrom=${encodeURIComponent(calledFrom ?? '')}&sessionId=${sessionId}&currentPage=${currentPage}`;

    return (
        <>
            {!isInAssessment && (
                <tr id="trQnButtons">
                    <td className="QnPanelT">
                        <table id="tabQnNav" className="QnPanel2T" style={{ height: "50px" }}>
                            <tbody>
                                <tr id="trQnOK" className="trQnOKT">
                                    <td id="QnPanelIdT" className="QnPanel">&nbsp;</td>
                                    <td id="QnPanelId2T" className="QnPanel">
                                        <div
                                            onClick={!lockAfterAnswer ? handleBack : undefined}
                                            style={{ display: lockAfterAnswer ? 'none' : 'block', cursor: 'pointer' }}
                                        >
                                            <div id="imgbtnBack" className="imgbtnBackT">
                                                <img
                                                    src={backSrc}
                                                    alt="Back"
                                                    onMouseOver={handleBackMouseOver}
                                                    onMouseOut={handleBackMouseOut}
                                                    onMouseDown={handleBackMouseDown}
                                                    onMouseUp={handleBackMouseUp}
                                                    className="imgbtnBack2T"
                                                />
                                            </div>
                                        </div>
                                    </td>
                                    <td id="QnPanelId3T" className="QnPanel">
                                        <input
                                            type="image"
                                            id="imgPrc"
                                            src={principle_n}
                                            className="imgPrcT"
                                            alt="Principle"
                                            style={{ display: !lockAfterAnswer && currentQnMeta?.NoOfPrc && currentQnMeta.NoOfPrc > 0 ? 'inline-block' : 'none' }}
                                            onClick={(e) => { e.preventDefault(); handlePrcClick(); }}
                                        />
                                    </td>
                                    <td id="QnPanelId4T" className="QnPanel">
                                        <input
                                            type="image"
                                            id="imgClue"
                                            src={clue_n}
                                            className="imgClueT"
                                            alt="Clue"
                                            style={{ display: !lockAfterAnswer && Number(currentQnMeta?.NoOfClues || 0) > 0 ? 'inline-block' : 'none' }}
                                            onClick={(e) => { e.preventDefault(); handleClueClick(); }}
                                        />
                                    </td>
                                    <td id="QnPanelId5T" className="QnPanel">
                                        <input type="image" id="imgSoln" src={solutionSrc} className="imgSolnT" alt="Solution" style={{ display: currentQnMeta?.NoOfSol && currentQnMeta.NoOfSol > 0 ? 'inline-block' : 'none' }} onClick={(e) => { e.preventDefault(); handleSolnClick(false); }} onMouseOver={handleSolutionMouseOver} onMouseOut={handleSolutionMouseOut} onMouseDown={handleSolutionMouseDown} />
                                    </td>
                                    <td id="QnPanelId5T" className="QnPanel">
                                        <a
                                            href={tutorialHref}
                                            id="imgbtnTut"
                                            className="imgbtnTutT"
                                            style={{ display: lockAfterAnswer ? 'none' : 'inline-block' }}
                                        >
                                            <img src={tutorials_n} alt="Tutorial" id="imgbtnTut2T" />
                                        </a>
                                    </td>
                                    <td id="QnPanelId6T" className="QnPanel">
                                        {isSubmitting ? (
                                            <Loading />
                                        ) : (
                                            okClicked ? (
                                                currentPage === totalPages ? (
                                                    <img src={doneSrc} alt="Done" style={{ verticalAlign: 'middle', cursor: 'pointer' }} onClick={handleDoneClick} onMouseOver={() => setDoneSrc(done_o)} onMouseOut={() => setDoneSrc(done_n)} />
                                                ) : (
                                                    <img src={nextSrc} alt="Next" onClick={goToNextPage} onMouseOver={handleNextMouseOver} onMouseOut={handleNextMouseOut} onMouseDown={handleNextMouseDown} onMouseUp={handleNextMouseUp} style={{ cursor: 'pointer', marginTop: '3px' }} />
                                                )
                                            ) : (
                                                <div onClick={handleOkClick} onMouseOver={handleOkMouseOver} onMouseOut={handleOkMouseOut} onMouseDown={handleOkMouseDown} onMouseUp={handleOkMouseUp} style={{ display: 'inline-block', cursor: 'pointer' }}>
                                                    <img src={okSrc} alt="OK" style={{ marginTop: '5px' }} />
                                                </div>
                                            )
                                        )}
                                    </td>
                                    <td id="QnPanelId7" className="QnPanel">&nbsp;</td>
                                </tr>
                                <tr id="trQnNext" className="trQnNextT">
                                    <td id="tdBN1" className="QnPanel">&nbsp;</td>
                                    <td id="tdAnsMsg" className="QnPanel">&nbsp;</td>
                                    <td id="tdShowCA" className="QnPanel">&nbsp;</td>
                                    <td id="tdAnsSoln" className="QnPanel">
                                        <img id="imgSolnAfterAnswering" src={solutionSrc} alt="Solution"
                                            onClick={(e) => { e.preventDefault(); handleSolnClick(); }}
                                            onMouseOver={handleSolutionMouseOver} onMouseOut={handleSolutionMouseOut} onMouseDown={handleSolutionMouseDown} />
                                    </td>
                                    <td id="tdQnNext" className="QnPanel">
                                        {okClicked ? (
                                            <input type="image" id="imgNextQuestion" src={nextSrc} alt="Next" onClick={goToNextPage} onMouseOver={handleNextMouseOver} onMouseOut={handleNextMouseOut} onMouseDown={handleNextMouseDown} onMouseUp={handleNextMouseUp} style={{ display: okClicked ? 'inline-block' : 'none' }} />
                                        ) : (
                                            <img id="imgOK" src={okSrc} alt="OK" onClick={handleOkClick} onMouseOver={handleOkMouseOver} onMouseOut={handleOkMouseOut} onMouseDown={handleOkMouseDown} onMouseUp={handleOkMouseUp} className="imgOKT" style={{ display: okClicked ? 'none' : 'inline-block' }} />
                                        )}
                                    </td>
                                    <td id="tdBN" className="QnPanel">&nbsp;</td>
                                </tr>
                            </tbody>
                        </table>
                    </td>
                </tr>
            )}

            <tr id="triFrame">
                <td id="tdFrameQnT" style={{ padding: 0, height: '100%' }}>
                    <div id="divFrameQn" style={{ height: 'calc(95vh - 50px)', width: '100%', display: 'flex', flexDirection: 'column' }}>
                        {questionHtml ? (
                            <iframe
                                id="iFrameQn"
                                ref={iframeRef}
                                frameBorder={0}
                                scrolling="yes"
                                className="CssiFrm"
                                style={{
                                    flex: '1 1 0',
                                    width: '100%',
                                    height: '100%',
                                    minHeight: 0,
                                    border: 'none',
                                    overflow: 'auto'
                                }}
                                title="Question Page"
                                onLoad={handleQuestionIframeLoad}
                            />
                        ) : <Loading />}
                    </div>
                </td>
            </tr>

            {ReactDOM.createPortal(
                <>
                    {showClueModal && (
                        <>
                            {!showSolnModal && (
                                <div
                                    id="divReadOnlyScreen"
                                    style={{ display: 'block' }}
                                    onClick={() => setShowClueModal(false)}
                                ></div>
                            )}
                            <div id="divClue" style={{ display: 'block' }}>
                                <table id="tblTitleClue" className="clue" cellSpacing="0" cellPadding="0" width="100%" style={{ height: "50px", fontSize: "15px" }}>
                                    <tbody>
                                        <tr>
                                            <td id="tdClueTitle1" align="left">Clue</td>
                                            <td id="tdClueClose" align="right" valign="top">
                                                <img
                                                    id="imgClueClose"
                                                    src={CloseBtnSrc}
                                                    alt="Close"
                                                    onClick={() => setShowClueModal(false)}
                                                    onMouseOver={handleCloseBtnMouseOver}
                                                    onMouseOut={handleCloseBtnMouseOut}
                                                    style={{ cursor: 'pointer', padding: '5px' }}
                                                />
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>

                                <div id="divFrameClue">
                                    {isClueLoading ? <Loading /> : (
                                        <iframe
                                            id="iFrameClue"
                                            ref={clueIframeRef}
                                            title="Clue"
                                            srcDoc={clueContent}
                                            style={{ width: '100%', border: 'none', minHeight: '80px' }}
                                            onLoad={() => {
                                                const iframe = clueIframeRef.current;
                                                if (!iframe || !iframe.contentDocument || !iframe.contentWindow) return;
                                                try {
                                                    const divShows = iframe.contentDocument.querySelectorAll("[id^='divShow']");
                                                    divShows.forEach(div => {
                                                        (div as HTMLElement).style.padding = "20px";
                                                    });

                                                    const cluesInput = iframe.contentDocument.getElementById('NoOfClues') as HTMLInputElement;
                                                    const totalClues = parseInt(cluesInput?.value || '0', 10);
                                                    setNoOfClues(totalClues);

                                                    const clueDivs = iframe.contentDocument.querySelectorAll<HTMLElement>('[id^="divShow"]');
                                                    clueDivs.forEach((div, index) => div.style.display = index === 0 ? 'block' : 'none');

                                                    const firstClueDiv = iframe.contentDocument.getElementById('divShow1');
                                                    if (firstClueDiv) {
                                                        const newHeight = firstClueDiv.offsetHeight;
                                                        iframe.style.height = `${Math.min(newHeight, 300)}px`;
                                                    } else if (totalClues === 0) {
                                                        iframe.style.height = '50px';
                                                    }
                                                } catch (e) { console.error("Error inside clue iframe onLoad:", e); }
                                            }}
                                        />
                                    )}
                                </div>

                                <table id="tblNavClue" className="clue" cellSpacing="0" cellPadding="0" width="100%" style={{ backgroundColor: '#F2F2F2' }}>
                                    <tbody>
                                        <tr>
                                            <td style={{ width: '33%' }}>&nbsp;</td>
                                            <td style={{ width: '34%' }}>&nbsp;</td>
                                            <td align="right" style={{ width: '33%', paddingRight: '15px' }}>
                                                {!isClueLoading && noOfClues > 1 && currentClueNo < noOfClues - 1 && (
                                                    <img
                                                        id="btnClueNext"
                                                        src={NextStepBtnSrc}
                                                        alt="Next Clue"
                                                        onClick={() => {
                                                            const nextClue = currentClueNo + 1;
                                                            const iframe = clueIframeRef.current;
                                                            if (iframe?.contentDocument) {
                                                                iframe.contentDocument.querySelectorAll<HTMLElement>('[id^="divShow"]').forEach((div, index) => {
                                                                    div.style.display = index === nextClue ? 'block' : 'none';
                                                                });
                                                                setCurrentClueNo(nextClue);

                                                                const nextClueDiv = iframe.contentDocument.getElementById(`divShow${nextClue + 1}`);
                                                                if (nextClueDiv) {
                                                                    const newHeight = nextClueDiv.scrollHeight + 20;
                                                                    iframe.style.height = `${Math.min(newHeight, 300)}px`;
                                                                }
                                                            }
                                                        }}
                                                        onMouseOver={handleNextStepBtnMouseOver}
                                                        onMouseOut={handleNextStepBtnMouseOut}
                                                        style={{ cursor: 'pointer' }}
                                                    />
                                                )}

                                                {!isClueLoading && (noOfClues <= 1 || currentClueNo === noOfClues - 1) && (
                                                    <img
                                                        id="btnClueGotIt"
                                                        src={GotItBtnSrc}
                                                        alt="Got It"
                                                        onClick={() => setShowClueModal(false)}
                                                        onMouseOver={handleGotItBtnMouseOver}
                                                        onMouseOut={handleGotItBtnMouseOut}
                                                        style={{ cursor: 'pointer', display: isClueLoading ? 'none' : 'inline-block' }}
                                                    />
                                                )}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                    {showPrcModal && (
                        <>
                            <div id="divReadOnlyScreen" style={{ display: 'block' }} onClick={() => setShowPrcModal(false)}></div>

                            <div id="divPCS" style={{ display: 'block' }} className="principle">

                                {/* Header */}
                                <table id="tblTitlePCS" className="principle" cellSpacing="0" cellPadding="0" width="100%">
                                    <tbody>
                                        <tr>
                                            <td id="tdPCSTitle1" style={{ paddingLeft: '15px', color: 'white', fontWeight: 'bold', fontSize: '24px' }}>
                                                Principle {principleList.length > 1 ? ` ${currentPrcIndex + 1} of ${principleList.length}` : ''}
                                            </td>
                                            <td id="tdPCSClose" align="right" valign="top">
                                                <img
                                                    id="imgPCSClose"
                                                    src={CloseBtnSrc}
                                                    alt="Close"
                                                    onClick={() => { setShowPrcModal(false); setIsPrcLoading(false); }}
                                                    onMouseOver={handleCloseBtnMouseOver}
                                                    onMouseOut={handleCloseBtnMouseOut}
                                                    style={{ cursor: 'pointer', padding: '5px', width: '40px', height: '40px', marginRight: '30px' }}
                                                />
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>

                                {/* Body */}
                                <div id="divFramePCS">
                                    {isPrcLoading ? <Loading /> : (
                                        <iframe
                                            id="iFramePrc"
                                            ref={prcIframeRef}
                                            title="Prc"
                                            srcDoc={prcContent}
                                            style={{ width: '100%', border: 'none', overflow: 'hidden', display: 'block' }}
                                            onLoad={() => {
                                                const iframe = prcIframeRef.current;
                                                if (!iframe || !iframe.contentDocument || !iframe.contentWindow) return;

                                                setTimeout(() => {
                                                    if (!iframe.contentDocument || !iframe.contentDocument.body) return;

                                                    const contentHeight = iframe.contentDocument.body.scrollHeight + 20;

                                                    const divPCS = document.getElementById('divPCS') as HTMLElement;
                                                    const titleTable = document.getElementById('tblTitlePCS') as HTMLElement;
                                                    const navTable = document.getElementById('tblNavPCS') as HTMLElement;
                                                    if (divPCS && titleTable && navTable) {
                                                        const maxModalHeight = window.innerHeight * 0.8;
                                                        const maxIframeHeight = maxModalHeight - titleTable.offsetHeight - navTable.offsetHeight;
                                                        if (contentHeight > maxIframeHeight) {

                                                            iframe.style.height = `${maxIframeHeight}px`;
                                                            iframe.style.overflowY = 'auto';
                                                        } else {

                                                            iframe.style.height = `${Math.max(contentHeight, 80)}px`;
                                                            iframe.style.overflowY = 'hidden';
                                                        }
                                                    } else {
                                                        iframe.style.height = `${Math.max(contentHeight, 80)}px`;
                                                    }
                                                }, 50);
                                            }}
                                        />
                                    )}
                                </div>

                                {/* Footer / Navigation */}
                                <table id="tblNavPCS" className="principle" cellSpacing="0" cellPadding="0" width="100%" style={{ height: '50px' }}>
                                    <tbody>
                                        <tr style={{ backgroundColor: '#F2F2F2' }}>
                                            {/* Previous Button */}
                                            <td align="left" style={{ width: '33%', paddingLeft: '15px' }}>
                                                {currentPrcIndex > 0 && (
                                                    <input
                                                        type='button'
                                                        value='PREVIOUS'
                                                        onClick={() => {
                                                            const prevIndex = currentPrcIndex - 1;
                                                            setCurrentPrcIndex(prevIndex);
                                                            const folderParam = new URLSearchParams(window.location.search).get('folder') || '';
                                                            const countryParam = params.get('country') || 'Australia';
                                                            fetchPrincipleContent(principleList[prevIndex], folderParam, countryParam).then(data => setPrcContent(data.principleHtml || ""));
                                                        }}
                                                        style={{
                                                            fontWeight: 'bold',
                                                            color: 'White',
                                                            fontSize: '12pt',
                                                            borderStyle: 'none',
                                                            backgroundColor: 'maroon',
                                                            cursor: 'pointer',
                                                            padding: '5px 0px'
                                                        }}
                                                    />
                                                )}
                                            </td>

                                            <td style={{ width: '34%' }}>&nbsp;</td>

                                            {/* Next / Got It Button */}
                                            <td align="right" style={{ width: '33%', paddingRight: '15px' }}>
                                                {currentPrcIndex < principleList.length - 1 ? (
                                                    <img
                                                        src={NextStepBtnSrc}
                                                        alt="Next"
                                                        onClick={() => {
                                                            const nextIndex = currentPrcIndex + 1;
                                                            setCurrentPrcIndex(nextIndex);
                                                            const folderParam = new URLSearchParams(window.location.search).get('folder') || '';
                                                            const countryParam = params.get('country') || 'Australia';
                                                            fetchPrincipleContent(principleList[nextIndex], folderParam, countryParam).then(data => setPrcContent(data.principleHtml || ""));
                                                        }}
                                                        onMouseOver={handleNextStepBtnMouseOver}
                                                        onMouseOut={handleNextStepBtnMouseOut}
                                                        style={{ cursor: 'pointer', width: '100px', height: '40px', marginRight: '30px', marginTop: '5px', marginBottom: '5px' }}
                                                    />
                                                ) : (
                                                    <img
                                                        src={GotItBtnSrc}
                                                        id="imgGotIt"
                                                        alt="Got It"
                                                        onClick={() => { setShowPrcModal(false); setIsPrcLoading(false); }}
                                                        onMouseOver={handleGotItBtnMouseOver}
                                                        onMouseOut={handleGotItBtnMouseOut}
                                                        style={{ cursor: 'pointer', width: '100px', height: '40px', marginRight: '30px', marginTop: '5px', marginBottom: '5px' }}
                                                    />
                                                )}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}

                    {showSolnModal && (
                        <>
                            <div id="divReadOnlyScreen" style={{ display: 'block' }} onClick={() => {
                                setShowSolnModal(false);
                            }}></div>
                            <div id="divPCS" style={{ display: 'block', position: 'fixed', bottom: '0', width: '100%', zIndex: 10000 }}>
                                <table id="tblTitlePCS" className="solution" cellSpacing="0" cellPadding="0" width="100%">
                                    <tbody>
                                        <tr>
                                            <td id="tdPCSTitle1">Step by step solution</td>
                                            <td id="tdPCSClose" align="right" valign="top">
                                                <button
                                                    type="button"
                                                    aria-label="Close solution"
                                                    onClick={() => setShowSolnModal(false)}
                                                    style={{ width: '30px', height: '30px', margin: '5px 15px 5px 0', padding: 0, border: 0, borderRadius: 0, backgroundColor: '#ff6500', color: '#fff', fontSize: '18px', lineHeight: '30px', textAlign: 'center', cursor: 'pointer' }}
                                                >
                                                    ×
                                                </button>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                                <div id="divFramePCS">
                                    {isSolnLoading ? <Loading /> : (
                                        <iframe
                                            id="iFrameSolution"
                                            ref={solnIframeRef}
                                            title="Solution"
                                            srcDoc={solnContent}
                                            style={{ width: '100%', border: 'none', minHeight: '50px' }}
                                            onLoad={() => {
                                                const iframe = solnIframeRef.current;
                                                if (!iframe || !iframe.contentDocument || !iframe.contentWindow) {
                                                    console.error("Iframe document or window not ready in onLoad.");
                                                    return;
                                                }
                                                try {
                                                    const iframeDoc = iframe.contentDocument;
                                                    const style = iframeDoc.createElement('style');
                                                    style.textContent = `
                                                /* Remove default spacing */
                                                table[id^="tblSol"] p,
                                                table[id^="tblSol"] table,
                                                table[id^="tblSol"] font {
                                                    margin: 0 !important;
                                                    padding: 0 !important;
                                                    line-height: normal !important;
                                                }

                                                /* Style the solution step rows */
                                                tr[id^="trSolStepLHS"] {
                                                    display: table-row;
                                                    background-color: rgb(255, 255, 255);
                                                    background-image: none;
                                                }

                                                /* First column (step content) - left side */
                                                tr[id^="trSolStepLHS"] > td:first-child {
                                                    width: 50% !important;
                                                    max-width: 50% !important;
                                                    vertical-align: top !important;
                                                    padding: 20px !important;
                                                    box-sizing: border-box !important;
                                                }

                                                /* Second column (answer/result) - right side */
                                                tr[id^="trSolStepLHS"] > td:nth-child(2) {
                                                    width: 50% !important;
                                                    max-width: 50% !important;
                                                    vertical-align: middle !important;
                                                    text-align: center !important;
                                                    padding: 20px !important;
                                                    box-sizing: border-box !important;
                                                }

                                                /* Content within cells */
                                                tr[id^="trSolStepLHS"] td * {
                                                    font-size: 24px !important;
                                                    font-family: Arial !important;
                                                    color: black !important;
                                                    font-weight: normal !important;
                                                    line-height: normal !important;
                                                }

                                                /* Make step labels bold */
                                                tr[id^="trSolStepLHS"] > td:first-child > font:first-child {
                                                    font-weight: bold !important;
                                                    margin-bottom: 10px !important;
                                                    display: block !important;
                                                }

                                                /* Images should not exceed container */
                                                tr[id^="trSolStepLHS"] img {
                                                    max-width: 100% !important;
                                                    height: auto !important;
                                                }

                                                /* Tables within cells */
                                                tr[id^="trSolStepLHS"] table {
                                                    width: 100% !important;
                                                    border-collapse: collapse !important;
                                                }

                                                /* Body overflow */
                                                body {
                                                    overflow-x: hidden !important;
                                                    margin: 0 !important;
                                                    padding: 0 !important;
                                                }
                                            `;
                                                    if (iframeDoc.head) {
                                                        iframeDoc.head.appendChild(style);
                                                    }
                                                    const stepsInput = iframeDoc.getElementById('NoOfSteps') as HTMLInputElement;
                                                    const totalSteps = parseInt(stepsInput?.value || '0', 10);
                                                    setNoOfSteps(totalSteps);

                                                    const isAutoExpanded = iframeDoc.getElementById('IsAutoExpanded');

                                                    const stepRows = iframeDoc.querySelectorAll<HTMLElement>('[id^="trSolStepLHS"]');
                                                    let firstStepHeight = 150;

                                                    stepRows.forEach((step, index) => {
                                                        if (index <= currentSolnNo || isAutoExpanded) {
                                                            step.style.display = 'table-row';
                                                        } else {
                                                            step.style.display = 'none';
                                                        }
                                                    });

                                                    if (totalSteps === 0) {
                                                        iframe.style.height = '50px';
                                                    }

                                                    setTimeout(() => {
                                                        if (!iframe.contentDocument || !iframe.contentDocument.body) return;

                                                        const contentHeight = iframe.contentDocument.body.scrollHeight + 5;

                                                        const divPCS = document.getElementById('divPCS') as HTMLElement;
                                                        const titleTable = document.getElementById('tblTitlePCS') as HTMLElement;
                                                        const navTable = document.getElementById('tblNavPCS') as HTMLElement;

                                                        if (divPCS && titleTable && navTable) {
                                                            const maxModalHeight = window.innerHeight * 0.45;
                                                            const maxIframeHeight = maxModalHeight - titleTable.offsetHeight - navTable.offsetHeight;

                                                            if (contentHeight > maxIframeHeight) {
                                                                iframe.style.height = `${maxIframeHeight}px`;
                                                                iframe.style.overflowY = 'auto';
                                                            } else {
                                                                iframe.style.height = `${Math.max(contentHeight, 50)}px`;
                                                                iframe.style.overflowY = 'hidden';
                                                            }
                                                        }

                                                        if (isAutoExpanded) {
                                                            setCurrentSolnNo(totalSteps - 1);
                                                            if (solutionSeen !== 'Y') {
                                                                setSolutionSeen('Y');
                                                            }
                                                            iframe.contentWindow?.scrollTo(0, iframe.contentDocument.body.scrollHeight);
                                                        } else {
                                                            if (stepRows.length > 0) {
                                                                firstStepHeight = stepRows[0].scrollHeight + 5;
                                                                iframe.style.height = `${Math.max(firstStepHeight, 150)}px`;
                                                                iframe.style.overflowY = 'hidden';
                                                            }
                                                        }
                                                    }, 50);

                                                    iframeDoc.querySelectorAll<HTMLImageElement>('img[id^="KsdImg"]').forEach(img => {
                                                        const index = img.id.substring(6);
                                                        const ksdDiv = iframeDoc.getElementById(`KsdDiv${index}`);
                                                        const content = ksdDiv?.getAttribute('data-ksd-content');
                                                        const wasPreviouslyRevealed = revealedSolnSteps.includes(Number(index));
                                                        const parentTd = img.closest('td');

                                                        if (content && parentTd && wasPreviouslyRevealed) {
                                                            parentTd.innerHTML = content;
                                                            img.setAttribute('alt', '2');
                                                            return;
                                                        }

                                                        img.onclick = () => {
                                                            if (content && parentTd && img.getAttribute('alt') === '1') {
                                                                parentTd.innerHTML = content;
                                                                img.setAttribute('alt', '2');
                                                                setRevealedSolnSteps(previous => previous.includes(Number(index)) ? previous : [...previous, Number(index)]);
                                                            }
                                                        };
                                                    });

                                                } catch (e) {
                                                    console.error("Error inside solution iframe onLoad:", e);
                                                }
                                            }}
                                        />
                                    )}
                                </div>
                                <table id="tblNavPCS" className="solution" cellSpacing="0" cellPadding="0" width="100%" style={{ backgroundColor: '#F2F2F2' }}>
                                    <tbody>
                                        <tr>
                                            <td align="left" style={{ width: '33%', paddingLeft: '15px' }}></td>
                                            <td style={{ width: '34%' }}>&nbsp;</td>
                                            <td align="right" style={{ width: '33%', paddingRight: '15px' }}>
                                                {!isSolnLoading && noOfSteps > 0 && currentSolnNo < noOfSteps - 1 && (
                                                    <img
                                                        id="btnPCSNext"
                                                        src={NextStepBtnSrc}
                                                        alt="Next Step"
                                                        onClick={() => {
                                                            const nextStep = currentSolnNo + 1;
                                                            const iframe = solnIframeRef.current;

                                                            if (iframe?.contentDocument?.body) {
                                                                const iframeDoc = iframe.contentDocument;

                                                                const stepRows = iframeDoc.querySelectorAll<HTMLElement>('[id^="trSolStepLHS"]');
                                                                if (nextStep >= stepRows.length) return;

                                                                const nextStepElement = stepRows[nextStep];
                                                                nextStepElement.style.display = 'table-row';

                                                                setTimeout(() => {
                                                                    if (!iframe.contentDocument || !iframe.contentDocument.body) return;

                                                                    const totalRequiredHeight = iframe.contentDocument.body.scrollHeight + 5;

                                                                    const divPCS = document.getElementById('divPCS') as HTMLElement;
                                                                    const titleTable = document.getElementById('tblTitlePCS') as HTMLElement;
                                                                    const navTable = document.getElementById('tblNavPCS') as HTMLElement;

                                                                    if (!divPCS || !titleTable || !navTable) return;

                                                                    const maxModalHeight = window.innerHeight * 0.45;
                                                                    const chromeHeight = titleTable.offsetHeight + navTable.offsetHeight;
                                                                    const maxAllowedIframeHeight = maxModalHeight - chromeHeight;

                                                                    if (totalRequiredHeight > maxAllowedIframeHeight) {
                                                                        iframe.style.height = `${maxAllowedIframeHeight}px`;
                                                                        iframe.style.overflowY = 'auto';
                                                                    } else {
                                                                        iframe.style.height = `${totalRequiredHeight}px`;
                                                                        iframe.style.overflowY = 'hidden';
                                                                    }

                                                                    iframe.contentWindow?.scrollTo(0, iframe.contentDocument.body.scrollHeight);

                                                                }, 50);

                                                                setCurrentSolnNo(nextStep);
                                                                if (solutionSeen !== 'Y') {
                                                                    setSolutionSeen(nextStep + 1);
                                                                }
                                                            }
                                                        }}
                                                        onMouseOver={handleNextStepBtnMouseOver}
                                                        onMouseOut={handleNextStepBtnMouseOut}
                                                        style={{ cursor: 'pointer' }}
                                                    />
                                                )}
                                                {!isSolnLoading && (noOfSteps === 0 || currentSolnNo === noOfSteps - 1) && (
                                                    <img
                                                        id="btnPCSClose"
                                                        src={GotItBtnSrc}
                                                        alt="Got It"
                                                        onClick={() => {
                                                            setShowSolnModal(false);
                                                            setSolutionSeen('Y');
                                                        }}
                                                        onMouseOver={handleGotItBtnMouseOver}
                                                        onMouseOut={handleGotItBtnMouseOut}
                                                        style={{ cursor: 'pointer', display: isSolnLoading ? 'none' : 'inline-block' }}
                                                    />
                                                )}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </>,
                document.body
            )}

        </>
    );
};

export default forwardRef(ImageQns);